/* eslint-disable import/no-extraneous-dependencies */
import FileContentUploaderButton from 'components/FileContentUploaderButton/ContentUploader';
import FileDownloadDropdown from 'components/FileDownloadDropdown/FileDownloadDropdown';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  showFailureToast,
  showSuccessToast,
  showWarningToast,
} from 'lib/ToastHelper';
// eslint-disable-next-line import/no-extraneous-dependencies
import { debounce } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { Row, Col, Input } from 'reactstrap';
import { companyIdAtom, HostActiveStatusFilterType } from 'views/state';

import DashboardActions from './DashboardActions';
import { getExportCsvUrlParams } from './helper';
import {
  activationKeyOptionsAtom,
  checkedListAtom,
  orderingAtom,
  paginationConfigAtom,
  searchPhraseFilterAtom,
  selectedCloudAgentsFilterAtom,
  serverOrWorkstationFilterAtom,
} from './state';
import { caRefreshAtom } from '../state';

const ServerOrWorkstationFilter = [
  { value: '', label: 'All Types' },
  { value: 'Server', label: 'All Servers' },
  { value: 'Workstations', label: 'All Workstations' },
];
const CloudAgentsFilterOptions = [
  { value: 'active', label: 'All Active' },
  { value: 'aging', label: 'Only In Aging Out Process' },
  { value: 'deleted', label: 'Deleted (Aged Out or Manual)' },
];

const colorStyles = {
  control: (styles) => ({
    ...styles,
    backgroundColor: 'white',
    cursor: 'pointer',
  }),
  option: (styles) => {
    // const color = chroma(data.color);
    return {
      ...styles,
      textAlign: 'left',
      cursor: 'pointer',
    };
  },
};

const Filters = () => {
  // PRESERVED STATE
  const companyId = useAtomValue(companyIdAtom);
  const agentActivationKeysOptions = useAtomValue(activationKeyOptionsAtom);
  const [hostTypeOptions, setHostTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [serverOrWorkstation, setServerOrWorkstation] = useAtom(
    serverOrWorkstationFilterAtom,
  );
  const [selectedCloudAgentsFilter, setSelectedCloudAgentsFilter] = useAtom(
    selectedCloudAgentsFilterAtom,
  );
  const [searchPhrase, setSearchPhrase] = useAtom(searchPhraseFilterAtom);
  const [checkedList, setCheckedList] = useAtom(checkedListAtom);
  const ordering = useAtomValue(orderingAtom);
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);

  const setCaRefresh = useSetAtom(caRefreshAtom);

  // NORMAL STATE
  const [searchPhrase2, setSearchPhrase2] = useState('');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const searchField = useRef<any>();

  useEffect(() => {
    const fetchHostOptions = async () => {
      // Add non-breaking spaces at the beginning of each label
      const formattedAgentKeys = agentActivationKeysOptions?.map((option) => ({
        ...option,
        label: '\u00A0\u00A0 ' + option.label, // Adding two non-breaking spaces
      }));

      // Filter options containing "Servers" and "Workstations"
      const workstationOptions = formattedAgentKeys?.filter((opt) =>
        opt.label.toLowerCase().includes('workstation'),
      );
      const serverOptions = formattedAgentKeys?.filter(
        (opt) => !opt.label.toLowerCase().includes('workstation'),
      );

      // Merge the lists in the correct order
      const newHostTypeOptions = [
        ServerOrWorkstationFilter[0], // "All Host Types"
        ServerOrWorkstationFilter[1], // "All Servers"
        ...(serverOptions || []), // Add all "Servers" options here
        ServerOrWorkstationFilter[2], // "All Workstations"
        ...(workstationOptions || []), // Add all "Workstations" options here
      ];

      setHostTypeOptions(newHostTypeOptions);
    };

    fetchHostOptions();
  }, [agentActivationKeysOptions]);

  useEffect(() => {
    if (searchPhrase !== searchPhrase2) setSearchPhrase2(searchPhrase);
  }, [searchPhrase]);

  const debouncedSearch = React.useRef(
    debounce(async (newSearchPhrase, oldSearchPhrase, setSearchPhrase) => {
      if (newSearchPhrase !== oldSearchPhrase) {
        setPaginationConfig({ ...paginationConfig, page: 1 });
        setSearchPhrase(newSearchPhrase);
        setCheckedList([]);
      }
    }, 1000),
  ).current;

  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const onSuccessBulkAlias = (response) => {
    setCaRefresh(Math.random().toString());
    if (
      response['cloud_agent_alias_csv'] ===
      response['cloud_agent_alias_updated']
    )
      showSuccessToast(response['message']);
    else showWarningToast(response['message']);
  };

  return (
    <Row className="text-left">
      <Col md="3" sm="12">
        <div className="d-flex align-items-center justify-content-end">
          <DashboardActions />
          <FileDownloadDropdown
            buttonId="ca_export_all"
            iconClass="fa fa-file-export"
            tooltip="Export CSV data for Cloud Agents"
            method="GET"
            config={[
              {
                url: 'cloud_agents/export-csv',
                label: 'Export Cloud Agents',
                fileName: `${localStorage
                  .getItem('currentCompany')
                  ?.toLowerCase()}_cloud_agents.csv`,
                urlParams: getExportCsvUrlParams(
                  companyId,
                  ordering,
                  serverOrWorkstation,
                  searchPhrase,
                  checkedList,
                  selectedCloudAgentsFilter,
                ),
              },
              {
                url: 'cloud_agents/export-csv',
                label: 'Export Aliases',
                fileName: `${localStorage
                  .getItem('currentCompany')
                  ?.toLowerCase()}_cloud_agent_aliases.csv`,
                urlParams: getExportCsvUrlParams(
                  companyId,
                  ordering,
                  serverOrWorkstation,
                  searchPhrase,
                  checkedList,
                  selectedCloudAgentsFilter,
                  'alias',
                ),
              },
              {
                url: 'cloud_agents/export-detail-csv',
                label: 'Export Installed Software',
                fileName: `${localStorage
                  .getItem('currentCompany')
                  ?.toLowerCase()}_cloud_agent_softwares.csv`,
                urlParams: getExportCsvUrlParams(
                  companyId,
                  ordering,
                  serverOrWorkstation,
                  searchPhrase,
                  checkedList,
                  selectedCloudAgentsFilter,
                  'ca_softwares',
                ),
              },
              {
                url: 'cloud_agents/export-detail-csv',
                label: 'Export Open Ports',
                fileName: `${localStorage
                  .getItem('currentCompany')
                  ?.toLowerCase()}_cloud_agent_open_ports.csv`,
                urlParams: getExportCsvUrlParams(
                  companyId,
                  ordering,
                  serverOrWorkstation,
                  searchPhrase,
                  checkedList,
                  selectedCloudAgentsFilter,
                  'ca_ports',
                ),
              },
            ]}
            onSuccessCallback={() =>
              showSuccessToast('File download completed.')
            }
            onFailureCallback={(message) => showFailureToast(message)}
          />
          <FileContentUploaderButton
            buttonId="bulkaliasButton"
            iconClass="fa fa-file-import"
            tooltip="Import/Update Cloud Agent Alias In Bulk"
            method="POST"
            uploadUrl="cloud_agents/alias/bulk/"
            allowedExtensions={['csv']}
            data={{ company_id: companyId }}
            onSuccessCallback={onSuccessBulkAlias}
            onFailureCallback={(message) => showFailureToast(message)}
            disabled={selectedCloudAgentsFilter === 'deleted'}
          />
        </div>
      </Col>
      <Col md="2" sm="12" className="text-left" style={{ paddingRight: '0px' }}>
        {/* <Label>Type</Label> */}
        <Select
          id="CloudAgentsTypeDropdown"
          className="custom-360-dropdown"
          value={hostTypeOptions.find((x) => x.value === serverOrWorkstation)}
          onChange={(e) => {
            if (e) {
              setPaginationConfig({ ...paginationConfig, page: 1 });

              setServerOrWorkstation(e.value);
              setCheckedList([]);
            }
          }}
          styles={colorStyles}
          options={hostTypeOptions}
        />
      </Col>
      <Col md="2" sm="12" className="text-left" style={{ paddingRight: '0px' }}>
        <Select
          id="CloudAgentsFilterDropdown"
          className="custom-360-dropdown"
          value={CloudAgentsFilterOptions.find(
            (x) => x.value === selectedCloudAgentsFilter,
          )}
          onChange={(e) => {
            if (e) {
              setPaginationConfig({ ...paginationConfig, page: 1 });
              setSelectedCloudAgentsFilter(
                e.value as HostActiveStatusFilterType,
              );
              setCheckedList([]);
            }
          }}
          styles={colorStyles}
          defaultValue={CloudAgentsFilterOptions[0]}
          options={CloudAgentsFilterOptions}
        />
      </Col>
      <Col md="5" sm="12" className="text-left">
        <Input
          ref={searchField}
          id="CloudAgentsSearch"
          type="text"
          list="data"
          value={searchPhrase2}
          placeholder="Search Cloud Agents"
          onChange={(e) => {
            setSearchPhrase2(e.target.value);
            debouncedSearch(e.target.value, searchPhrase, setSearchPhrase);
          }}
        />
      </Col>
    </Row>
  );
};

export default Filters;
